import type { ReactNode } from "react";
import { emojiForShortcode } from "@/lib/emoji";

/** Lookup for `<@U…>` mentions when the API did not label them (`<@U…|Name>`). */
export type SlackTextContext = { userNames: Map<string, string>; selfUserId?: string };

const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

function decodeEntities(text: string): string {
  return text.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&");
}

function renderAngleToken(inner: string, key: string, ctx: SlackTextContext): ReactNode {
  const [target = "", label] = inner.split("|", 2) as [string, string | undefined];
  if (target.startsWith("@")) {
    const id = target.slice(1);
    const name = label || ctx.userNames.get(id);
    const mine = Boolean(ctx.selfUserId && id === ctx.selfUserId);
    return (
      <span key={key} className={mine ? "chat-mention chat-mention-me" : "chat-mention"} title={name ? undefined : id}>
        @{name || id}
      </span>
    );
  }
  if (target.startsWith("#")) {
    return (
      <span key={key} className="chat-mention">
        #{label || "channel"}
      </span>
    );
  }
  if (target.startsWith("!")) {
    const special = target.slice(1);
    if (special === "here" || special === "channel" || special === "everyone") {
      return (
        <span key={key} className="chat-mention">
          @{special}
        </span>
      );
    }
    const fallback = label ? decodeEntities(label) : "";
    return fallback ? (
      <span key={key} className={special.startsWith("subteam") ? "chat-mention" : undefined}>
        {fallback}
      </span>
    ) : null;
  }
  const href = decodeEntities(target);
  const text = decodeEntities(label || target.replace(/^mailto:/i, ""));
  if (!SAFE_HREF.test(href)) return decodeEntities(`<${inner}>`);
  return (
    <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="chat-link">
      {text}
    </a>
  );
}

// Order matters: angle tokens and inline code win over emphasis inside them.
const INLINE =
  /<([^<>\n]+)>|`([^`\n]+)`|(?<![\w*])\*(?!\s)([^*\n]+?)\*(?![\w*])|(?<![\w_])_(?!\s)([^_\n]+?)_(?![\w_])|(?<![\w~])~(?!\s)([^~\n]+?)~(?![\w~])|:([a-z0-9_+-]+):|(https?:\/\/[^\s<>]+[^\s<>.,;:!?)\]'"])/gi;

function renderInline(text: string, ctx: SlackTextContext, keyPrefix: string, depth = 0): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let index = 0;
  const matches = [...text.matchAll(INLINE)];
  for (const match of matches) {
    const start = match.index ?? 0;
    if (start > last) out.push(decodeEntities(text.slice(last, start)));
    const key = `${keyPrefix}-${index++}`;
    const [whole, angle, code, bold, italic, strike, shortcode, bareUrl] = match;
    if (angle !== undefined) {
      out.push(renderAngleToken(angle, key, ctx));
    } else if (code !== undefined) {
      out.push(
        <code key={key} className="chat-code">
          {decodeEntities(code)}
        </code>,
      );
    } else if (bold !== undefined) {
      out.push(<strong key={key}>{depth < 3 ? renderInline(bold, ctx, key, depth + 1) : bold}</strong>);
    } else if (italic !== undefined) {
      out.push(<em key={key}>{depth < 3 ? renderInline(italic, ctx, key, depth + 1) : italic}</em>);
    } else if (strike !== undefined) {
      out.push(<s key={key}>{depth < 3 ? renderInline(strike, ctx, key, depth + 1) : strike}</s>);
    } else if (shortcode !== undefined) {
      out.push(emojiForShortcode(shortcode) ?? whole);
    } else if (bareUrl !== undefined) {
      const href = decodeEntities(bareUrl);
      out.push(
        <a key={key} href={href} target="_blank" rel="noopener noreferrer" className="chat-link">
          {href}
        </a>,
      );
    }
    last = start + whole.length;
  }
  if (last < text.length) out.push(decodeEntities(text.slice(last)));
  return out;
}

/** Slack mrkdwn (mentions, links, *bold*, _italic_, ~strike~, `code`, ```blocks```) → React. */
export function renderSlackText(text: string, ctx: SlackTextContext): ReactNode[] {
  const out: ReactNode[] = [];
  const parts = text.split(/```/);
  parts.forEach((part, i) => {
    if (i % 2 === 1 && i < parts.length - 1) {
      out.push(
        <pre key={`pre-${i}`} className="chat-code-block">
          {decodeEntities(part.replace(/^\n/, ""))}
        </pre>,
      );
      return;
    }
    const chunk = i % 2 === 1 ? `\`\`\`${part}` : part;
    const lines = chunk.split("\n");
    lines.forEach((line, j) => {
      if (j > 0) out.push("\n");
      const quote = /^&gt;\s?/.exec(line);
      if (quote) {
        out.push(
          <span key={`q-${i}-${j}`} className="chat-quote">
            {renderInline(line.slice(quote[0].length), ctx, `q-${i}-${j}`)}
          </span>,
        );
      } else {
        out.push(...renderInline(line, ctx, `l-${i}-${j}`));
      }
    });
  });
  return out;
}

const IMAGE_URL = /^https:\/\/(?:media\d*\.giphy\.com\/|i\.giphy\.com\/|[^\s]+\.(?:gif|png|jpe?g|webp)(?:\?[^\s]*)?$)/i;

/** Direct image links in a message body (e.g. a GIF sent as a URL) shown inline in the bubble. */
export function imageLinksInText(text: string): string[] {
  const urls: string[] = [];
  for (const match of text.matchAll(/<(https:\/\/[^|>\s]+)(?:\|[^>]*)?>|(https:\/\/[^\s<>]+)/gi)) {
    const url = decodeEntities(match[1] || match[2] || "");
    if (url && IMAGE_URL.test(url) && !urls.includes(url)) urls.push(url);
  }
  return urls;
}

/** True when the body is only image link(s), so the bubble can show just the image. */
export function textIsOnlyImageLinks(text: string): boolean {
  const stripped = text
    .replace(/<https:\/\/[^>\s]+>/gi, (token) => (imageLinksInText(token).length ? "" : token))
    .replace(/https:\/\/[^\s<>]+/gi, (url) => (imageLinksInText(url).length ? "" : url))
    .trim();
  return !stripped && imageLinksInText(text).length > 0;
}
